import { HoverableLinkSizes } from '@paywithextend/components'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { VerifiedSecondaryEmailToastMessage } from 'src/app/Toast/Toast'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import ErrorList from 'src/components/ErrorList/ErrorList'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { useFlyoutDispatch, useFormFields } from 'src/hooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { closeModal } from 'src/navigation/Lnk'
import {
  secondaryEmailsMutations,
  secondaryEmailsQueryKeys,
} from 'src/queries/secondaryEmails/secondaryEmails.queries'
import { createSetIsAddSecondaryEmailPopupOpen } from 'src/redux/actions/app.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { parseBackendErrors } from 'src/utils/error.utils'
import { stringIsEmpty } from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

export type SecondaryEmailFlatFields = {
  email?: string
}

export type SecondaryEmailVerificationFlatFields = {
  verificationCode?: string
}

interface VerifySecondaryEmailFormProps {
  className?: string
  secondaryEmail: string
}

const VerifySecondaryEmailForm: React.FC<VerifySecondaryEmailFormProps> = (
  props,
) => {
  const translate = useTranslate()

  const locale = useSelector(selectNullSafeLocaleFromState)

  const dispatch = useFlyoutDispatch()
  const queryClient = useQueryClient()

  const verifyMutation = useMutation({
    ...secondaryEmailsMutations.verify(),
    // Invalidation lives on the useMutation callback so it always fires even if
    // the popup unmounts before settle (mirrors the legacy success refetch).
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: secondaryEmailsQueryKeys.list(),
      })
    },
  })
  const resendMutation = useMutation(
    secondaryEmailsMutations.resendVerification(),
  )

  const resendVerificationCodeLoading = resendMutation.isPending
  const resendVerificationCodeTopLevelError = resendMutation.error
    ? parseBackendErrors(resendMutation.error).topLevelError
    : undefined
  const verifySecondaryEmailLoading = verifyMutation.isPending
  const verifySecondaryEmailTopLevelError = verifyMutation.error
    ? parseBackendErrors(verifyMutation.error).topLevelError
    : undefined

  const { analytics } = useAnalyticsContext()

  const {
    fields: secondaryEmailVerificationFlatFields,
    fieldsErrors: secondaryEmailVerificationFlatFieldsErrors,
    onFieldChange: onSecondaryEmailVerificationFlatFieldChange,
    setErrorsAndReturnDidPassValidation:
      secondaryEmailVerificationFlatFieldsSetErrors,
  } = useFormFields<SecondaryEmailVerificationFlatFields>(
    {},
    () => ({
      verificationCode: [stringIsEmpty()],
    }),
    undefined,
  )

  const verifyCodeAndSaveEmail = (): void => {
    if (
      secondaryEmailVerificationFlatFieldsSetErrors() &&
      secondaryEmailVerificationFlatFields.verificationCode
    ) {
      analytics.track('secondary email verification - verify code')
      verifyMutation.mutate(
        {
          email: props.secondaryEmail,
          code: secondaryEmailVerificationFlatFields.verificationCode,
        },
        {
          // UI-only side effects stay on the mutate() callback (skipped if the
          // component unmounts mid-flight), matching the legacy thunk's
          // close-popup / close-modal / open-toast sequence.
          onSuccess: () => {
            dispatch(createSetIsAddSecondaryEmailPopupOpen(false))
            dispatch(closeModal())
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: <VerifiedSecondaryEmailToastMessage />,
              }),
            )
          },
        },
      )
    }
  }

  return (
    <>
      <FieldLabel>{translate('Secondary Email')}</FieldLabel>
      {props.secondaryEmail}
      <TextInputField
        css={css`
          margin-top: 20px;
        `}
        fieldLabel={
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: flex-end;
              width: 100%;
            `}
          >
            {translate('Verification Code')}
            <HoverableLink
              underline
              regular
              size={HoverableLinkSizes.Medium}
              ariaLabelSource={'Resend code'}
              onClick={(): void => {
                analytics.track('secondary email verification - resend code')
                resendMutation.mutate({ email: props.secondaryEmail })
              }}
              disabled={resendVerificationCodeLoading}
            >
              {translate('Resend code')}
            </HoverableLink>
          </div>
        }
        placeholder={translateSingleString(locale, 'Enter code')}
        design={TextInputFieldDesigns.New}
        onChange={(val: string): void =>
          onSecondaryEmailVerificationFlatFieldChange('verificationCode', val)
        }
        errors={secondaryEmailVerificationFlatFieldsErrors['verificationCode']}
      />
      {resendVerificationCodeTopLevelError && (
        <ErrorList
          errors={[resendVerificationCodeTopLevelError]}
          css={css`
            margin-top: 8px;
          `}
        />
      )}
      <div
        css={css`
          margin-top: 40px;
          display: flex;
          gap: 20px;
          align-items: center;
        `}
      >
        <Button
          type={ButtonTypes.button}
          design={ButtonDesigns.designCta}
          big={true}
          text={translate('Verify Code and Save Email')}
          onClick={verifyCodeAndSaveEmail}
          loading={verifySecondaryEmailLoading}
        />
        <HoverableLink
          underline
          ariaLabelSource={'Cancel'}
          onClick={(): void => {
            analytics.track('secondary email verification - cancel')
            dispatch(createSetIsAddSecondaryEmailPopupOpen(false))
          }}
        >
          {translate('Cancel')}
        </HoverableLink>
      </div>
      {verifySecondaryEmailTopLevelError && (
        <ErrorList
          errors={[verifySecondaryEmailTopLevelError]}
          css={css`
            margin-top: 8px;
          `}
        />
      )}
    </>
  )
}

export default VerifySecondaryEmailForm
